"use client";

import * as React from "react";
import { HugeiconsIcon, HugeiconsIconProps } from "@hugeicons/react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

/**
 * ConfirmActionDialog — shared confirmation prompt
 *
 * One dialog for every "Are you sure?" moment in the product. Wraps the
 * design-system AlertDialog with the unified Flozy modal layout:
 *
 *   Header:  [icon] [title]                                            [X]
 *   ───────────────────────────────────────────────────────────────
 *   Body:    description + optional richer content (lists, callouts)
 *   ───────────────────────────────────────────────────────────────
 *   Footer:                                            [Cancel] [Confirm]
 *
 * Two usage patterns:
 *   1. Controlled — pass `open` + `onOpenChange`
 *   2. Uncontrolled — pass `trigger` element
 *
 * Tone controls the icon badge wash and the confirm button variant:
 *   - "destructive" (default) → soft red wash, red confirm button
 *   - "default"               → soft blue wash, primary confirm button
 */

type Tone = "destructive" | "default";

export interface ConfirmActionDialogProps {
  icon?: HugeiconsIconProps["icon"];
  tone?: Tone;
  title: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  confirmLabel?: React.ReactNode;
  cancelLabel?: React.ReactNode;
  onConfirm?: () => void;
  loading?: boolean;
  disabled?: boolean;

  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactNode;
}

export function ConfirmActionDialog({
  icon,
  tone = "destructive",
  title,
  description,
  children,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  onConfirm,
  loading = false,
  disabled = false,
  open,
  onOpenChange,
  trigger,
}: ConfirmActionDialogProps) {
  const mediaTone =
    tone === "destructive"
      ? "bg-destructive/10 text-destructive"
      : "bg-accent text-accent-foreground";

  const actionVariant = tone === "destructive" ? "destructive" : "default";

  // Render body section only if there's something to show
  const hasBody = !!description || !!children;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      {trigger ? (
        <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      ) : null}
      <AlertDialogContent>
        <AlertDialogHeader>
          {icon ? (
            <AlertDialogMedia className={cn(mediaTone)}>
              <HugeiconsIcon icon={icon} strokeWidth={1.75} />
            </AlertDialogMedia>
          ) : null}
          <AlertDialogTitle>{title}</AlertDialogTitle>
        </AlertDialogHeader>

        {hasBody && (
          <AlertDialogBody>
            {description ? (
              <AlertDialogDescription>{description}</AlertDialogDescription>
            ) : null}
            {children ? (
              <div className="text-sm text-muted-foreground">{children}</div>
            ) : null}
          </AlertDialogBody>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            variant={actionVariant}
            disabled={disabled || loading}
            onClick={(e) => {
              if (onConfirm) {
                e.preventDefault();
                onConfirm();
              }
            }}
          >
            {loading
              ? typeof confirmLabel === "string"
                ? `${confirmLabel.replace(/\.{3}$/, "")}...`
                : confirmLabel
              : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
